import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Alert,
  TextStyle,
  FlatList,
  View,
  ViewStyle,
  TouchableOpacity,
} from 'react-native'

import { CommentReadModel, useStores, setPageTitle } from '@trivie/core'
import {
  Avatar,
  color,
  Comment,
  Header,
  iconArrowBack,
  iconArrowFullDown,
  Post,
  spacing,
  SvgIcon,
  Text,
  TextInputBubble,
} from '@trivie/ui-mobile'
import { useHistory, useParams } from 'react-router'
import { SortComment } from './sortComment'
import './comments-screen.css'

export const CommentsScreen = observer(() => {
  const { talkStore, userStore } = useStores()
  const [selectedComment, setSelectedComment] = useState<
    undefined | Instance<typeof CommentReadModel>
  >(undefined)
  const [editingComment, setEditingComment] = useState<
    undefined | Instance<typeof CommentReadModel>
  >(undefined)
  const [showSortModal, setShowSortModal] = useState(false)
  const {
    editComment,
    selectedPost,
    commentOnPost,
    commentOnComment,
    likePost,
    sortedComments,
    likeComment,
    setCommentSort,
    commentSortDisplay,
    deleteComment,
    refreshPosts,
    refreshPost,
  } = talkStore

  const { user, checkNotifications } = userStore

  const history = useHistory()

  // const [showReply, setShowReply] = useState(false)
  const [loading, setLoading] = useState(true)
  // @ts-ignore
  const params = useParams()
  // @ts-ignore
  const { postID } = params

  useEffect(() => {
    talkStore.refreshPost(postID).then(() => {
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (selectedPost?.author) {
      setPageTitle(
        `Comment by ${selectedPost.author.first_name || ''} - ${process.env.REACT_APP_SITE_TITLE}`,
      )
    }
  }, [selectedPost])

  const goBack = () => history.go(-1)

  const onComment = () => {}

  const onPostComment = (comment: any) => {
    if (editingComment) {
      editComment(editingComment.id, comment.text)
      setEditingComment(undefined)
      setSelectedComment(undefined)
    } else if (selectedComment) {
      commentOnComment(comment, selectedPost.id, selectedComment.id, user!.id).then(() => {
        talkStore.refreshPost(selectedPost.id)
      })
      setSelectedComment(undefined)
    } else if (user) {
      commentOnPost(comment, selectedPost.id, user.id).then(() => {
        talkStore.refreshPost(selectedPost.id)
        checkNotifications(user.id)
      })
    }
  }

  const onSort = () => {
    setShowSortModal(!showSortModal)
  }

  const onLongPress = (comment: Instance<typeof CommentReadModel>) => {
    setSelectedComment(comment)
  }
  const onSortChange = (sort: 'new' | 'discussions' | 'top') => {
    setCommentSort(sort)
    // @ts-ignore
    setShowSortModal(false)
  }

  const onReply = (comment?: Instance<typeof CommentReadModel>) => {
    if (comment) {
      setSelectedComment(comment)
    }
    commentRef.current?.open()
  }

  const onDelete = () => {
    Alert.alert('Delete Comment', 'Are you sure you want to permanently remove this comment?', [
      {
        text: 'Cancel',
        onPress: () => {
          setSelectedComment(undefined)
        },
      },
      {
        text: 'Delete',
        onPress: () => {
          if (selectedComment) {
            deleteComment(selectedComment)
            setSelectedComment(undefined)
          }
        },
      },
    ])
  }

  const renderPost = (post: any) => {
    const photos = [] as any
    let video
    if (post.resources && post.resources.length > 0) {
      post.resources.forEach((resource: any) => {
        if (
          resource.extension.toLowerCase() === 'jpeg' ||
          resource.extension.toLowerCase() === 'jpg' ||
          resource.extension.toLowerCase() === 'png' ||
          resource.extension.toLowerCase() === 'gif'
        ) {
          photos.push(resource)
        } else {
          video = resource
        }
      })
    }

    return photos && photos.length > 0 ? (
      <Post
        type="photo"
        title={`${post.author.first_name} ${post.author.last_name}`}
        subtitle={post.title}
        avatar={post.author.file_url}
        post={post}
        onComment={onComment}
        onDelete={onDelete}
        onTogglePin={() => {
          refreshPosts()
          refreshPost(selectedPost.id)
        }}
        onLike={() => likePost(selectedPost.id)}
        photos={talkStore.selectedPostPhoto}
        isDetail
      />
    ) : video ? (
      <Post
        type="video"
        title={`${post.author.first_name} ${post.author.last_name}`}
        subtitle={post.title}
        avatar={post.author.file_url}
        post={post}
        onComment={onComment}
        onLike={() => likePost(selectedPost.id)}
        onDelete={onDelete}
        onTogglePin={() => {
          refreshPosts()
          refreshPost(selectedPost.id)
        }}
        video={video}
        isDetail
        index={1}
        currentViewableIndex={2}
      />
    ) : (
      <Post
        type="text"
        title={`${selectedPost.author.first_name} ${selectedPost.author.last_name}`}
        subtitle={selectedPost.title}
        avatar={selectedPost.author.file_url}
        post={selectedPost}
        onComment={onComment}
        onLike={() => likePost(selectedPost.id)}
        onDelete={onDelete}
        onTogglePin={() => {
          refreshPosts()
          refreshPost(selectedPost.id)
        }}
        isDetail
      />
    )
  }

  const renderHeader = () => {
    return (
      <View>
        {renderPost(selectedPost)}
        <TouchableOpacity
          // @ts-ignore
          dataSet={{ qat: `sort_comments_btn` }}
          accessibilityRole="button"
          accessibilityLabel="Sort"
          onPress={onSort}
        >
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: spacing[2],
              backgroundColor: color.palette.panel,
              paddingLeft: spacing[4],
            }}
          >
            <svg
              width={20}
              height={20}
              fill={color.palette.blue}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6.4 9.2h.2c.77 0 1.4.63 1.4 1.4v7c0 .77-.63 1.4-1.4 1.4h-.2c-.77 0-1.4-.63-1.4-1.4v-7c0-.77.63-1.4 1.4-1.4zM12 5c.77 0 1.4.63 1.4 1.4v11.2c0 .77-.63 1.4-1.4 1.4-.77 0-1.4-.63-1.4-1.4V6.4c0-.77.63-1.4 1.4-1.4zm5.6 8c.77 0 1.4.63 1.4 1.4v3.2c0 .77-.63 1.4-1.4 1.4-.77 0-1.4-.63-1.4-1.4v-3.2c0-.77.63-1.4 1.4-1.4z" />
            </svg>
            <Text
              style={{ color: color.shade30, marginLeft: spacing[2], marginRight: 4 }}
              variant="body2Emphasized"
              text={commentSortDisplay}
            />
            <SvgIcon src={iconArrowFullDown} color="black" size={24} />
          </View>
        </TouchableOpacity>
      </View>
    )
  }

  const renderComment = ({ item }: any) => {
    return (
      item && (
        <Comment
          key={item.id}
          comment={item}
          onLike={() => likeComment(item)}
          onPressOpenMenu={item.author?.id === user!.id ? () => onLongPress(item) : () => {}}
          onReply={() => onReply(item)}
        >
          <View style={{ paddingLeft: spacing[6] }}>
            {item.child_comments &&
              item.child_comments.map((c: any) => (
                <>
                  <Comment
                    key={c.id}
                    comment={c}
                    onLike={() => likeComment(item)}
                    onPressOpenMenu={c.author?.id === user!.id ? () => onLongPress(c) : () => {}}
                    isChildComment
                  />
                </>
              ))}
          </View>
        </Comment>
      )
    )
  }

  const commentRef = useRef() as any

  if (!selectedPost) {
    return null
  }

  return (
    <View style={FULL}>
      <div style={CONTAINER}>
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <Header
              leftIcon={iconArrowBack}
              onLeftPress={goBack}
              center
              titleStyle={HEADER}
              txOptions={{ author: selectedPost.author.first_name }}
              headerTx={loading ? '' : 'Discuss.usersComment'}
            />
          </div>
        </div>

        {loading ? (
          <View
            style={{ flex: 1, top: -60, display: 'flex', height: '100%', justifyContent: 'center' }}
          >
            <ActivityIndicator size="small" />
          </View>
        ) : (
          <>
            <FlatList
              contentContainerStyle={{ paddingBottom: 120 }}
              data={sortedComments}
              extraData={talkStore.test}
              ListHeaderComponent={renderHeader()}
              renderItem={renderComment}
              keyExtractor={(item) => item?.id.toString()}
            />

            <div
              style={{ flexDirection: 'column', paddingTop: 28, position: 'sticky', bottom: 12 }}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}
              >
                <Avatar
                  size="small"
                  style={{ marginRight: spacing[2], marginTop: spacing[1] }}
                  source={user!.photo_url}
                  name={`${user?.first_name} ${user?.last_name}`}
                />
                <View style={{ flex: 1, flexDirection: 'row' }}>
                  <TextInputBubble
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row', //column
                    }}
                    buttonStyle={{
                      bottom: 10,
                      height: '100%',
                      justifyContent: 'flex-end',
                    }}
                    defaultValue={editingComment ? editingComment.text : ''}
                    placeholderTx="Discuss.writeComment"
                    onPost={onPostComment}
                  />
                </View>
              </View>
            </div>
          </>
        )}
      </div>
      {showSortModal && (
        <SortComment
          setLoading={setLoading}
          onSortChange={onSortChange}
          onClose={() => {
            setShowSortModal(false)
          }}
          open={showSortModal}
        />
      )}
    </View>
  )
})

const FULL: ViewStyle = { height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }
const CONTAINER: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: color.white,
}
const HEADER: TextStyle = { fontSize: 24, color: color.black }
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
